import { useEffect, useState } from "react";
import { useNotification } from "../../NotificationContext";
import {
  getVisit,
  saveVisit,
  sendFollowUpWhatsApp,
  updateVisit,
} from "../../apis/visit";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { getEvents } from "../../apis/event";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Visit } from "../../interfaces/Visit";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "../../components/ConfirmationModal";
import { getSettings } from "../../apis/setting";
import { Setting } from "../../interfaces/Setting";
import { getPatient } from "../../apis/patient";

type FollowUpVisitType = Omit<
  Visit,
  "visitStatusId" | "clinicVisitType" | "advice" | "amountPaid" | "visitFiles"
>;

const AddFollowUpVisit: React.FC<{
  closeModal: () => void;
  patientId: any;
  userId: number;
  parentId: any;
  clinicVisitTypeId: any;
  clinicVisitTypeLayoutId: any;
  chiefComplaint: any;
}> = ({
  closeModal,
  patientId,
  userId,
  parentId,
  clinicVisitTypeId,
  clinicVisitTypeLayoutId,
  chiefComplaint,
}) => {
  const [visit, setVisit] = useState<FollowUpVisitType>({
    userId,
    isInpatient: true,
    patientId: patientId,
    clinicVisitTypeId: clinicVisitTypeId,
    parentId: parentId,
    clinicVisitTypeLayoutId: clinicVisitTypeLayoutId,
    meta: {
      height: "",
      weight: "",
    },
    fee: 0,
    discount: 0,
    medicines: [],
    services: [],
    symptoms: [],
    provisionalDiagnosis: [],
    finalDiagnosis: [],
    date: format(new Date(), "yyy-MM-dd HH:mm").toString(),
    chiefComplaint: chiefComplaint,
    visitStatus: "scheduled",
    invoice: null,
  });
  const { showNotification } = useNotification();
  const [errors, setErrors] = useState<any>();
  const [visitId, setVisitId] = useState<any>();
  const [isPostponeVisit, setIsPostponeVisit] = useState(false);
  const [weeklyOffDays, setWeeklyOffDays] = useState<Array<number>>([]);
  const [openTime, setOpenTime] = useState<Date | any>();
  const [closeTime, setCloseTime] = useState<Date | any>();
  const [isOpen24Hours, setIsOpen24Hours] = useState<boolean>(false);
  const [holidayDates, setHolidayDates] = useState<Array<Date>>([]);
  const [sendFollowUpMessageEnabled, setSendFollowUpMessageEnabled] =
    useState(false);
  const [isWhatsAppConfirmOpen, setIsWhatsAppConfirmOpen] = useState(false);
  const [createdFollowUpVisitId, setCreatedFollowUpVisitId] = useState<
    number | null
  >(null);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [navigateAfterWhatsApp, setNavigateAfterWhatsApp] = useState(false);
  const [whatsAppPhone, setWhatsAppPhone] = useState("");
  const [whatsAppPhoneError, setWhatsAppPhoneError] = useState<string | null>(
    null
  );

  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
    fetchEvents();
  }, []);

  const digitsOnlyPhone = (value: string) => value.replace(/\D/g, "").slice(0, 10);

  const validateForm = () => {
    const validationErrors: any = {};

    if (!visit?.date || isNaN(new Date(visit.date).getTime())) {
      validationErrors.date = "* date is required";
    }

    const hasErrors = Boolean(Object.keys(validationErrors).length);

    return { hasErrors, validationErrors };
  };

  const finishAfterSave = async (
    savedVisit?: { id?: number; patient?: { phone?: string } } | null,
    options?: { navigateToVisits?: boolean }
  ) => {
    const id = savedVisit?.id ?? null;
    if (sendFollowUpMessageEnabled && id) {
      let patientPhone = digitsOnlyPhone(
        String(savedVisit?.patient?.phone ?? "")
      );

      if (patientPhone.length !== 10 && patientId) {
        try {
          const patientRes: any = await getPatient(patientId);
          const patient = patientRes?.data?.data ?? patientRes?.data ?? patientRes;
          patientPhone = digitsOnlyPhone(String(patient?.phone ?? ""));
        } catch {
          // keep empty; user can type the number
        }
      }

      setWhatsAppPhone(patientPhone);
      setWhatsAppPhoneError(null);
      setCreatedFollowUpVisitId(id);
      setNavigateAfterWhatsApp(Boolean(options?.navigateToVisits));
      setIsWhatsAppConfirmOpen(true);
      return;
    }
    closeModal();
    if (options?.navigateToVisits) {
      navigate("/visits");
    }
  };

  const handleWhatsAppConfirmClose = () => {
    const shouldNavigate = navigateAfterWhatsApp;
    setIsWhatsAppConfirmOpen(false);
    setCreatedFollowUpVisitId(null);
    setNavigateAfterWhatsApp(false);
    setWhatsAppPhone("");
    setWhatsAppPhoneError(null);
    closeModal();
    if (shouldNavigate) {
      navigate("/visits");
    }
  };

  const handleSendWhatsApp = async () => {
    if (!createdFollowUpVisitId) {
      handleWhatsAppConfirmClose();
      return;
    }
    const phone = digitsOnlyPhone(whatsAppPhone);
    if (phone.length !== 10) {
      setWhatsAppPhoneError("Enter a valid 10-digit WhatsApp number");
      return;
    }
    setWhatsAppPhoneError(null);
    setSendingWhatsApp(true);
    try {
      await sendFollowUpWhatsApp(createdFollowUpVisitId, phone);
      showNotification(
        "success",
        "Follow-up WhatsApp sent to patient",
        "",
        "topRight"
      );
      handleWhatsAppConfirmClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        "Failed to send follow-up WhatsApp";
      showNotification("error", msg, "", "topRight");
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const createFollowUpVisit = async () => {
    const { hasErrors, validationErrors } = validateForm();
    if (hasErrors) {
      setErrors(validationErrors);
      return;
    }
    setErrors(null);
    saveVisit({
      ...visit,
      date: new Date(visit.date).toISOString(),
      is_scheduled: true,
      visitStatus: "scheduled",
      chiefComplaint,
    })
      .then((res: any) => {
        showNotification(
          "success",
          "Appointment Added successfully",
          "",
          "topRight"
        );
        finishAfterSave(res);
      })
      .catch((err) => {
        setErrors(err.response?.data?.errors);

        const message = err?.response?.data?.message;
        if (message) {
          const existingVisitId = message.split("|")[1];
          if (existingVisitId) {
            setVisitId(existingVisitId);
            setIsPostponeVisit(true);
          }
        } else {
          showNotification(
            "error",
            "An error occurred while saving the visit",
            "",
            "topRight"
          );
        }
      });
  };

  const handleDateSelect = (date: any) => {
    setVisit({
      ...visit,
      date: format(new Date(date), "yyy-MM-dd HH:mm").toString(),
    });
  };

  const handleColor = (time: Date) => {
    return time.getHours() > 12 ? "text-success" : "text-error";
  };

  const handlePostponeVisit = async () => {
    let serialNumber;
    await getVisit(visitId).then((res: any) => {
      serialNumber = res.serialNumber;
    });

    try {
      await updateVisit({
        ...visit,
        serialNumber: serialNumber,
        date: new Date(visit.date).toISOString(),
        id: visitId,
      });
      showNotification("success", "Visit updated successfully", "", "topRight");
      closeModal();
    } catch (err: any) {
      const message =
        err?.data?.message || "An error occurred while updating the visit";
      showNotification("error", message, "", "topRight");
    }
  };

  const createNewFollowupVisit = async () => {
    saveVisit({
      ...visit,
      date: new Date(visit.date).toISOString(),
      is_scheduled: false,
      visitStatus: "scheduled",
    })
      .then((res: any) => {
        showNotification(
          "success",
          "Appointment Added successfully",
          "",
          "topRight"
        );
        finishAfterSave(res, { navigateToVisits: true });
      })
      .catch(() => {});
  };

  const fetchSettings = () => {
    getSettings().then((res: any) => {
      const settings = res.data.data;
      const weeklyOffSetting = settings.find(
        (setting: Setting) => setting.code === "weekly_off_days"
      );

      if (weeklyOffSetting) {
        const offDays = weeklyOffSetting.value
          .split(",")
          .map((day: string) => {
            const dayMapping: Record<string, number> = {
              Sunday: 0,
              Monday: 1,
              Tuesday: 2,
              Wednesday: 3,
              Thursday: 4,
              Friday: 5,
              Saturday: 6,
            };
            return dayMapping[day.trim()] ?? null;
          })
          .filter((day: any) => day !== null);
        setWeeklyOffDays(offDays);
      }

      const openTimeSetting = settings.find(
        (setting: any) => setting.code === "open_time"
      );
      const closeTimeSetting = settings.find(
        (setting: any) => setting.code === "close_time"
      );

      const isOpen24HoursSetting = settings.find(
        (setting: any) => setting.code === "is_open_24_hours"
      );

      const followUpMessageSetting = settings.find(
        (setting: Setting) => setting.code === "send_follow_up_message"
      );
      setSendFollowUpMessageEnabled(followUpMessageSetting?.value === "yes");

      if (isOpen24HoursSetting?.value === "yes") {
        setIsOpen24Hours(true);
      }

      if (openTimeSetting && closeTimeSetting) {
        const openTimeValue = convertTo24Hour(openTimeSetting.value);
        const closeTimeValue = convertTo24Hour(closeTimeSetting.value);

        setOpenTime(openTimeValue);
        setCloseTime(closeTimeValue);
      }
    });
  };

  const convertTo24Hour = (time: string) => {
    const [hours, minutes] = time.match(/\d+/g)!;
    const isPM = time.includes("PM");
    const hours24 = isPM ? (parseInt(hours) % 12) + 12 : parseInt(hours) % 12;
    return `${hours24.toString().padStart(2, "0")}:${minutes}`;
  };

  const isDayDisabled = (date: Date) => {
    return !weeklyOffDays.includes(date.getDay());
  };

  const isTimeDisabled = (time: Date) => {
    const currentTime = `${time.getHours().toString().padStart(2, "0")}:${time
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
    if (isOpen24Hours) {
      return true;
    } else {
      return !(currentTime < openTime || currentTime > closeTime);
    }
  };

  const fetchEvents = () => {
    const startDate = format(startOfMonth(new Date()), "yyyy-MM-dd");
    const endDate = format(endOfMonth(new Date()), "yyyy-MM-dd");
    getEvents({
      start: startDate,
      end: endDate,
      per_page: 1000,
    }).then((res: any) => {
      const events = res?.data;
      const holidayEvents = events?.filter(
        (event: any) => event?.eventCategory?.name === "holiday"
      );

      if (holidayEvents && holidayEvents.length > 0) {
        const holidays = holidayEvents.map(
          (event: any) => new Date(event.startDate)
        );
        setHolidayDates(holidays);
      }
    });
  };

  return (
    <div className="">
      <div className="pt-2">
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Date
        </label>
        <div className="mt-1">
          <DatePicker
            className="w-full block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            showTimeSelect
            selected={new Date(visit.date)}
            onChange={(date) => handleDateSelect(date)}
            popperPlacement="bottom-start"
            portalId="root-panel"
            dateFormat="dd/MM/yyyy hh:mm a"
            timeClassName={handleColor}
            filterDate={isDayDisabled}
            filterTime={isTimeDisabled}
            excludeDates={holidayDates}
          />
          {errors && errors.date && (
            <span className="text-sm text-red-500 leading-0">
              {errors.date}
            </span>
          )}
        </div>
      </div>

      <div className="flex gap-x-6 justify-start items-center py-4 border-gray-900/10">
        <button
          onClick={() => createFollowUpVisit()}
          type="submit"
          className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dark"
        >
          Save
        </button>
        <button
          onClick={() => closeModal()}
          type="button"
          className="text-sm font-semibold leading-6 text-gray-900"
        >
          Cancel
        </button>
      </div>
      {isPostponeVisit && (
        <ConfirmationModal
          message="You have already scheduled this visit with the same doctor on same date. Do you want to postpone this visit?"
          onClose={() => setIsPostponeVisit(false)}
          onSubmit={() => {
            handlePostponeVisit();
            setIsPostponeVisit(false);
          }}
          okLabel="Yes"
          cancelLabel="Cancel"
          noLabel="No"
          onNo={() => {
            setIsPostponeVisit(false);
            createNewFollowupVisit();
          }}
        />
      )}
      {isWhatsAppConfirmOpen && (
        <div
          className="fixed inset-0 z-50 flex flex-1 items-center justify-center overflow-y-auto"
          aria-labelledby="whatsapp-modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              aria-hidden="true"
            />
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <h3
                  className="text-lg font-medium leading-6 text-gray-900"
                  id="whatsapp-modal-title"
                >
                  Send WhatsApp follow-up?
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Follow-up visit saved. Confirm the patient&apos;s WhatsApp
                  number below, or change it before sending.
                </p>
                <div className="mt-4">
                  <label
                    htmlFor="whatsapp-phone"
                    className="block text-sm font-medium text-gray-700"
                  >
                    WhatsApp number
                  </label>
                  <div className="relative mt-1 rounded-md shadow-sm">
                    <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-gray-500">
                      +91
                    </span>
                    <input
                      id="whatsapp-phone"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={whatsAppPhone}
                      onChange={(e) => {
                        setWhatsAppPhone(digitsOnlyPhone(e.target.value));
                        setWhatsAppPhoneError(null);
                      }}
                      disabled={sendingWhatsApp}
                      placeholder="10-digit mobile"
                      className="block w-full rounded-md border-0 py-1.5 pl-12 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                    />
                  </div>
                  {whatsAppPhoneError && (
                    <p className="mt-1 text-sm text-red-500">
                      {whatsAppPhoneError}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-5 flex gap-3 sm:mt-6">
                <button
                  type="button"
                  disabled={sendingWhatsApp}
                  onClick={handleSendWhatsApp}
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 sm:text-sm"
                >
                  {sendingWhatsApp ? "Sending…" : "Yes, send WhatsApp"}
                </button>
                <button
                  type="button"
                  disabled={sendingWhatsApp}
                  onClick={handleWhatsAppConfirmClose}
                  className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 sm:text-sm"
                >
                  No
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default AddFollowUpVisit;
